import type { ImportMode, ImportProgress } from './qobuzPlaylistImportTypes';
import { authedFetch, markInitialQobuzImportDone } from './qobuzPlaylistImportCore';
import { emitQobuzPlaylistImportProgress } from './qobuzPlaylistImportProgress';

export async function runQobuzPlaylistImportLoop(
  mode: ImportMode,
  onProgress?: (p: ImportProgress) => void,
): Promise<ImportProgress> {
  let offset = 0;
  let imported = 0;
  let updated = 0;
  let skippedUserEdited = 0;
  let skippedUnchanged = 0;
  let totalFound = 0;
  const maxIterations = 60;

  for (let i = 0; i < maxIterations; i++) {
    let res: Response;
    try {
      res = await authedFetch('/api/user/playlists/import/qobuz', 'POST', { offset, mode });
    } catch (err: any) {
      const progress: ImportProgress = {
        imported, updated, skippedUserEdited, skippedUnchanged,
        playlistsProcessed: offset, totalFound,
        done: true,
        error: err?.message || 'Network error during import',
      };
      emitQobuzPlaylistImportProgress(progress);
      onProgress?.(progress);
      return progress;
    }

    if (!res.ok) {
      let errMsg = `Import failed (HTTP ${res.status})`;
      let code: string | undefined;
      try {
        const body: any = await res.json();
        errMsg = body?.error || errMsg;
        code = body?.code;
      } catch {}
      const progress: ImportProgress = {
        imported, updated, skippedUserEdited, skippedUnchanged,
        playlistsProcessed: offset, totalFound,
        done: true,
        error: code === 'qobuz_not_connected' ? 'Qobuz not connected' : errMsg,
      };
      emitQobuzPlaylistImportProgress(progress);
      onProgress?.(progress);
      return progress;
    }

    const data: any = await res.json();
    imported += data.batchImported ?? 0;
    updated += data.batchUpdated ?? 0;
    skippedUserEdited += data.batchSkippedUserEdited ?? 0;
    skippedUnchanged += data.batchSkipped ?? 0;
    totalFound = data.totalFound ?? totalFound;
    offset = data.nextOffset ?? offset;

    const progress: ImportProgress = {
      imported, updated, skippedUserEdited, skippedUnchanged,
      playlistsProcessed: offset,
      totalFound,
      done: !data.moreAvailable,
    };
    emitQobuzPlaylistImportProgress(progress);
    onProgress?.(progress);

    if (!data.moreAvailable) {
      markInitialQobuzImportDone();
      try {
        window.dispatchEvent(new CustomEvent('maestro:playlists-updated', {
          detail: { source: 'qobuz-import', ...progress },
        }));
      } catch {}
      return progress;
    }
  }

  const progress: ImportProgress = {
    imported, updated, skippedUserEdited, skippedUnchanged,
    playlistsProcessed: offset, totalFound,
    done: true,
    error: 'Import cap reached — some playlists may be missing',
  };
  emitQobuzPlaylistImportProgress(progress);
  onProgress?.(progress);
  return progress;
}
